import { useMemo } from "react";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip } from "chart.js";
import { IcCar } from "@racoon/icons";
import { Namespace, useTypedT } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import type { TruckProviderGroup } from "../../dashboard/lib/use-trucks-by-provider.hook";

ChartJS.register(ArcElement, Tooltip);

export const TruckProviderPanel = ({ groups }: { groups: TruckProviderGroup[] }) => {
  const t = useTypedT(Namespace.Govdash);
  const total = useMemo(() => groups.reduce((sum, group) => sum + group.truckCount, 0), [groups]);

  const chartData = useMemo(
    () => ({
      labels: groups.map((group) => group.providerName),
      datasets: [
        {
          data: groups.map((group) => group.truckCount),
          backgroundColor: groups.map((group) => group.color),
          borderWidth: 0,
        },
      ],
    }),
    [groups],
  );

  return (
    <CardContentWrapper hasDivider={false} className="flex flex-1 min-h-0 flex-col overflow-hidden">
      <div className="flex shrink-0 flex-col">
        <div className="flex items-center gap-2 p-4">
          <p className="min-w-0 flex-1 truncate text-base font-semibold text-text-primary">
            {t("monitor.trucksOnLine")}
          </p>
          <IcCar className="size-4 shrink-0 text-text-primary" />
          <span className="shrink-0 text-base font-semibold text-text-primary">{total}</span>
        </div>
        <div className="h-px bg-slate-200" />
      </div>

      {groups.length > 0 && (
        <div className="flex shrink-0 items-center justify-center border-b border-slate-200 py-2">
          <div className="size-[100px]">
            <Pie
              data={chartData}
              options={{ maintainAspectRatio: false, plugins: { legend: { display: false } } }}
            />
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-4">
        {groups.map((group) => (
          <div key={group.providerId.toString()} className="flex h-4 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-1">
              <span className="size-3 shrink-0 rounded-full" style={{ backgroundColor: group.color }} />
              <p className="truncate text-sm font-medium text-text-primary">{group.providerName}</p>
            </div>
            <p className="shrink-0 text-sm font-medium text-text-primary">{group.truckCount}</p>
          </div>
        ))}
      </div>
    </CardContentWrapper>
  );
};
